/**
 * MuteCreatorButton.jsx — Consumer-only button to toggle push-notification mute
 * for a specific creator.
 *
 * Behaviour:
 *  - Hidden for anonymous users and for creators themselves (viewing own profile).
 *  - Shows nothing while the initial status check is loading to avoid flicker.
 *  - Default state: "receiving" (bell icon, label "Silenciar notificaciones").
 *  - Muted state:   "muted"     (bell-off icon, label "Volver a recibir").
 *
 * Props:
 *   creatorId  string — target creator user_id
 *   isSelf     bool   — true when logged-in user is the creator (button hidden)
 *   userRole   string — "consumer" | "creator" | "model" | "admin" (only consumer renders)
 */

import { useState, useEffect } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";
export function MuteCreatorButton({ creatorId, isSelf, userRole }) {
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!creatorId || isSelf || userRole !== "consumer") {
      setLoading(false);
      return;
    }
    axios.get(`${API}/me/creator-mutes/${creatorId}/status`, { withCredentials: true })
      .then(r => setMuted(!!r.data?.muted))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [creatorId, isSelf, userRole]);

  if (isSelf || userRole !== "consumer" || loading) return null;

  const handleToggle = async () => {
    setSaving(true);
    try {
      if (muted) {
        await axios.delete(`${API}/me/creator-mutes/${creatorId}`, { withCredentials: true });
        setMuted(false);
        toast.success("Volverás a recibir notificaciones de esta creadora.");
      } else {
        await axios.post(`${API}/me/creator-mutes/${creatorId}`, {}, { withCredentials: true });
        setMuted(true);
        toast.success("No recibirás más notificaciones de esta creadora.");
      }
    } catch {
      toast.error("Error al guardar la preferencia.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={saving}
      className={`flex items-center gap-1.5 text-xs rounded-xl px-3 py-1.5 border transition-all disabled:opacity-60 font-body ${
        muted
          ? "border-zinc-700 bg-zinc-900/60 text-zinc-400 hover:text-white hover:border-zinc-600"
          : "border-primary/30 bg-primary/5 text-primary hover:bg-primary/15 hover:border-primary/60"
      }`}
      data-testid={`mute-creator-${creatorId}`}
      title={muted
        ? "Actualmente silenciada. Pulsa para volver a recibir notificaciones."
        : "Dejar de recibir notificaciones de esta creadora cuando cambie su perfil o esté en directo."}
    >
      {saving
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : (muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />)}
      <span>{muted ? "Volver a recibir" : "Silenciar"}</span>
    </button>
  );
}

export default MuteCreatorButton;
